/**
 * Offline Contact Cache Service
 *
 * Caches the full contact list so field operators can look up phone numbers
 * and emails without connectivity. This is a FULL sync, not a delta sync --
 * GET /api/contacts has no `updated_since` filter (see contactListQuerySchema).
 *
 * MVP cached fields: name, phone, email, organization, department,
 * ics_position, contact_type, is_active, photo_url, clearance_level,
 * preferred_channel.
 */

import { db, CachedContact } from './db';
import { api } from '@/services/api';
import { photoCacheService } from './photo-cache.service';

const SYNC_META_KEY = 'contact_cache_last_sync';

/** Page size -- must stay <= contactListQuerySchema's `limit` max of 200. */
const PAGE_SIZE = 200;

/**
 * Absolute ceiling on requests per sync (50 * 200 = 10,000 contacts).
 * This is a backstop against a future server contract change resurrecting an
 * unbounded loop -- it is NOT the normal termination condition.
 */
const MAX_PAGES = 50;

/** In-flight sync per tenant, so concurrent callers coalesce (see cacheFromServer). */
const inFlightByTenant = new Map<string, Promise<{ cached: number; photos: number }>>();

async function runCacheFromServer(
  tenantId: string
): Promise<{ cached: number; photos: number }> {
  const result = { cached: 0, photos: 0 };

  try {
    // Fetch all active contacts, paginated.
    //
    // WHY `page` AND NOT `offset`: the list route validates its query with
    // validate(contactListQuerySchema, 'query'), and that middleware REPLACES
    // req.query with the Zod-parsed object. Zod strips unknown keys, so an
    // `offset` param never reaches ContactService.list -- the server silently
    // falls back to page=1 and returns the SAME first 200 contacts forever.
    // With `hasMore` driven only off `contacts.length === limit`, that was a
    // non-terminating request loop: it ended only when a request finally threw,
    // i.e. after the operator burned the shared 600/min apiRateLimiter bucket
    // and started getting 429s across the whole app mid-incident -- while
    // contacts 201+ never reached the offline cache at all. Terminate off the
    // server's own `totalPages`, and cap iterations unconditionally.
    let pageNo = 1;
    const limit = PAGE_SIZE;
    let hasMore = true;

    const allContacts: any[] = [];

    while (hasMore && pageNo <= MAX_PAGES) {
      const params = new URLSearchParams();
      params.append('limit', String(limit));
      params.append('page', String(pageNo));

      // Bare body at the top level -- there is no `meta` envelope.
      const res = await api.get<{
        data: any[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>(`/contacts?${params}`);

      const contacts: any[] = res?.data ?? [];

      for (const c of contacts) {
        const cached: CachedContact = {
          id: c.id,
          tenant_id: tenantId,
          name: c.name ?? '',
          contact_type: c.contact_type ?? 'person',
          phone: c.phone,
          email: c.email,
          organization: c.organization,
          department: c.department,
          ics_position: c.ics_position,
          is_active: c.is_active ?? true,
          photo_url: c.photo_url,
          // Cache the REAL handling caveat / preferred channel. The list query
          // is SELECT c.*, so both are already in this body. Leaving them out
          // forced the offline list to invent values, and it invented the
          // permissive ones ('internal'/'email') -- see CachedContact in db.ts.
          clearance_level: c.clearance_level,
          preferred_channel: c.preferred_channel,
          updated_at: c.updated_at ?? new Date().toISOString(),
          is_synced: true,
          synced_at: new Date().toISOString(),
        };

        await db.cachedContacts.put(cached);
        result.cached++;
        allContacts.push(c);
      }

      // Primary termination: the server's own page count. Fall back to the
      // "short page" heuristic only if totalPages is missing/unusable, and
      // keep `contacts.length === limit` as a secondary guard either way.
      const totalPages = Number(res?.totalPages);
      const serverHasMorePages =
        Number.isFinite(totalPages) && totalPages > 0 ? pageNo < totalPages : true;

      hasMore = contacts.length === limit && serverHasMorePages;
      pageNo++;
    }

    if (hasMore) {
      // Cap tripped -- never fail silently, this means the contract changed.
      console.warn(
        `[ContactCache] Stopped after ${MAX_PAGES} pages (${result.cached} contacts); ` +
          'server still reported more. Offline contact cache may be incomplete.'
      );
    }

    // Cache photo blobs for offline rendering
    const photoItems = allContacts
      .filter((c) => c.photo_url)
      .map((c) => ({ id: c.id, photoUrl: c.photo_url }));
    result.photos = await photoCacheService.cachePhotos('contact', photoItems);

    // Persist the sync timestamp
    localStorage.setItem(
      `${SYNC_META_KEY}:${tenantId}`,
      new Date().toISOString()
    );
  } catch (error) {
    console.warn('[ContactCache] Failed to cache from server:', error);
  }

  return result;
}

/**
 * Fetch contacts from the server and store/update them in IndexedDB.
 *
 * Concurrent calls for the same tenant coalesce into a single sync. Callers
 * fire this un-awaited on every debounced search keystroke / filter change
 * (useContactsData) as well as on app mount (useOfflineCache), which would
 * otherwise stack overlapping full syncs and multiply the request volume.
 */
async function cacheFromServer(tenantId: string): Promise<{ cached: number; photos: number }> {
  const existing = inFlightByTenant.get(tenantId);
  if (existing) return existing;

  const pending = runCacheFromServer(tenantId).finally(() => {
    if (inFlightByTenant.get(tenantId) === pending) {
      inFlightByTenant.delete(tenantId);
    }
  });
  inFlightByTenant.set(tenantId, pending);

  return pending;
}

/**
 * Query cached contacts from IndexedDB with optional text search.
 * Searches across name, phone, email, organization, department, ics_position.
 */
async function getCached(
  tenantId: string,
  search?: string
): Promise<CachedContact[]> {
  let results = await db.cachedContacts
    .where('tenant_id')
    .equals(tenantId)
    .toArray();

  if (search) {
    const lower = search.toLowerCase();
    results = results.filter((c) => {
      return (
        c.name?.toLowerCase().includes(lower) ||
        c.phone?.toLowerCase().includes(lower) ||
        c.email?.toLowerCase().includes(lower) ||
        c.organization?.toLowerCase().includes(lower) ||
        c.department?.toLowerCase().includes(lower) ||
        c.ics_position?.toLowerCase().includes(lower)
      );
    });
  }

  // Sort by name
  results.sort((a, b) => a.name.localeCompare(b.name));

  return results;
}

/**
 * Get a single cached contact by ID.
 */
async function getById(id: string): Promise<CachedContact | undefined> {
  return db.cachedContacts.get(id);
}

/**
 * Return the ISO timestamp of the last successful sync for this tenant,
 * or null if never synced.
 */
function getLastSyncTime(tenantId: string): string | null {
  return localStorage.getItem(`${SYNC_META_KEY}:${tenantId}`);
}

/**
 * Clear all cached contacts (e.g. on logout).
 */
async function clearCache(): Promise<void> {
  await db.cachedContacts.clear();
  await photoCacheService.clearCache('contact');
}

export const contactCacheService = {
  cacheFromServer,
  getCached,
  getById,
  getLastSyncTime,
  clearCache,
};
